import { createUseStyles } from 'react-jss';
import { Theme } from '@mui/material';

interface StyleProps {
    columns: number;
}

const gridColumns = (columns: number) => `repeat(${columns}, minmax(0, 1fr))`;

export const useStyles = createUseStyles<string, StyleProps>((theme: any) => {
    const { color, radius, mq, motion } = (theme as Theme).custom;
    const base = (columns: number) => ({ display: 'grid', gridTemplateColumns: gridColumns(columns), gap: 12 });
    return {
        grid: ({ columns }: StyleProps) => ({
            ...base(columns),
            padding: 12,
            [mq.md]: { gridTemplateColumns: gridColumns(Math.min(columns, 4)) },
            [mq.sm]: { gridTemplateColumns: gridColumns(2), gap: 8, padding: 8 },
        }),
        groupGrid: ({ columns }: StyleProps) => ({
            ...base(columns),
            [mq.md]: { gridTemplateColumns: gridColumns(Math.min(columns, 4)) },
            [mq.sm]: { gridTemplateColumns: gridColumns(2), gap: 8 },
        }),
        // The grid scrolls on its own so the filters and the pager stay in view; on phones the page scrolls instead.
        scroller: {
            maxHeight: 'calc(100vh - 380px)',
            minHeight: 320,
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            [mq.sm]: { maxHeight: 'none', minHeight: 0 },
        },
        skeleton: { aspectRatio: '1', borderRadius: radius.md },
        stackTile: {
            position: 'relative',
            display: 'block',
            width: 'calc(100% - 6px)',
            padding: 0,
            marginBottom: 6,
            border: `1px solid ${color.border}`,
            borderRadius: radius.md,
            backgroundColor: color.surface,
            color: 'inherit',
            fontFamily: 'inherit',
            textAlign: 'left',
            cursor: 'pointer',
            boxShadow: `3px 3px 0 -1px ${color.surface}, 3px 3px 0 0 ${color.borderStrong}, 6px 6px 0 -1px ${color.surface}, 6px 6px 0 0 ${color.border}`,
            transitionProperty: 'border-color',
            transitionDuration: motion.fast,
            '&:hover': { borderColor: color.primary.main },
            '&:focus-visible': { outline: `2px solid ${color.focusRing}`, outlineOffset: 2 },
        },
        stackMedia: { position: 'relative', aspectRatio: '1', borderRadius: radius.md, overflow: 'hidden', backgroundColor: color.surfaceMuted },
        stackImage: { display: 'block', width: '100%', height: '100%', objectFit: 'contain' },
        stackEmpty: { position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: color.text.secondary },
        stackBadges: { position: 'absolute', top: 8, left: 8, right: 8, display: 'flex', flexWrap: 'wrap', gap: 4 },
        stackFooter: {
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 8,
            padding: '14px 10px 8px',
            fontSize: 12,
            fontWeight: 600,
            color: color.tooltip.text,
            background: `linear-gradient(transparent, ${color.overlay})`,
        },
        group: {
            gridColumn: '1 / -1',
            border: `1px solid ${color.border}`,
            borderRadius: radius.lg,
            backgroundColor: color.surfaceMuted,
            padding: 8,
        },
        groupHeader: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, padding: '2px 4px 8px' },
        groupToggle: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '6px 8px',
            border: 'none',
            background: 'transparent',
            fontFamily: 'inherit',
            fontSize: 14,
            color: color.text.primary,
            cursor: 'pointer',
            borderRadius: radius.sm,
            '&:hover': { backgroundColor: color.surface },
            '&:focus-visible': { outline: `2px solid ${color.focusRing}` },
        },
        groupCount: { fontSize: 12, color: color.text.secondary, fontVariantNumeric: 'tabular-nums' },
        groupActions: { marginLeft: 'auto' },
        pagination: { '& .MuiTablePagination-toolbar': { minHeight: 40, paddingLeft: 0 } },
    };
});
